/**
 * Web Vitals instrumentation — captures LCP, INP, CLS, TTFB and FCP
 * per route and logs them to the console in production.
 *
 * Integration point: replace `reportMetric` with a beacon to your
 * analytics endpoint (e.g., Datadog RUM, Sentry, custom API).
 */
import type { Metric } from "web-vitals";

function getRoute(): string {
  return window.location.pathname || "/";
}

function reportMetric(metric: Metric) {
  const payload = {
    name: metric.name,
    value: Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value),
    rating: metric.rating, // "good" | "needs-improvement" | "poor"
    route: getRoute(),
    navigationType: metric.navigationType,
    timestamp: new Date().toISOString(),
  };

  // In development, log to console for visibility
  if (import.meta.env.DEV) {
    const color =
      metric.rating === "good"
        ? "color: #0cce6b"
        : metric.rating === "needs-improvement"
          ? "color: #ffa400"
          : "color: #ff4e42";
    console.log(
      `%c[Web Vital] ${metric.name}: ${payload.value}${metric.name === "CLS" ? " (x1000)" : "ms"} [${metric.rating}] — ${payload.route}`,
      color,
    );
    return;
  }

  // Production: no analytics endpoint configured yet — skip the network
  // call entirely instead of generating 404s on every page load. When a
  // real endpoint is wired up, replace this with a sendBeacon() call.
  void payload;
}

export async function initWebVitals() {
  try {
    const { onLCP, onINP, onCLS, onTTFB, onFCP } = await import("web-vitals");
    onLCP(reportMetric);
    onINP(reportMetric);
    onCLS(reportMetric);
    onTTFB(reportMetric);
    onFCP(reportMetric);
  } catch {
    // web-vitals not available — silently ignore
  }
}
